import * as React from 'react'
import cn from 'classnames'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { Overlay } from '../../atoms/Overlay'
import { Button } from '../../atoms/Button'
import styles from './ConfirmationDialog.module.scss'
import { fadeVariant } from '../../../utils/variants'
import { motion } from 'framer-motion'

export type ConfirmationDialogProps = {
  /**
   * Text to show as the title of the dialog
   */
  title?: string
  /**
   * Text to show as the body of the dialog
   */
  body?: string
  /**
   * Text to show as the label of the confirm button
   * @default "Confirm"
   */
  confirmLabel?: string
  /**
   * Text to show as the label of the cancel button
   * @default "Cancel"
   */
  cancelLabel?: string
  /**
   * Colour variant for the confirm button
   */
  confirmColourVariant?: 'theme' | 'brand' | 'danger' | 'paper' | 'highlight'
  /**
   * Colour variant for the cancel button (outline)
   */
  cancelColourVariant?: 'theme' | 'brand' | 'danger' | 'paper' | 'highlight'
  /**
   * When true, renders confirm button before cancel button
   * @default false
   */
  reverseButtons?: boolean
  /**
   * Callback to be called once the dialog has been confirmed. If the dialog is cancelled, this won't be called
   */
  onConfirm?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void> | void
  /**
   * Callback to be called once the dialog has been cancelled.
   */
  onCancel?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void> | void
  /**
   * Component to use as a trigger
   */
  children?: JSX.Element
  contentClassName?: string
  showOverlay?: boolean
} & Pick<AlertDialog.AlertDialogProps, 'onOpenChange' | 'defaultOpen' | 'open'>

export const Root: React.VFC<ConfirmationDialogProps> = (props) => {
  const {
    onOpenChange,
    open,
    defaultOpen,
    onConfirm,
    onCancel,
    title,
    body,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmColourVariant,
    cancelColourVariant,
    reverseButtons = false,
    contentClassName,
    showOverlay = true,
  } = props

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
    >
      {children}
      <AlertDialog.Portal>
        {showOverlay && (
          <AlertDialog.Overlay asChild>
            <Overlay />
          </AlertDialog.Overlay>
        )}
        <AlertDialog.Content className={cn(styles.content, contentClassName)}>
          <motion.div
            variants={fadeVariant}
            initial="inactive"
            animate="active"
            exit="inactive"
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            {title && (
              <AlertDialog.Title className={styles.title}>
                {title}
              </AlertDialog.Title>
            )}
            {body && (
              <AlertDialog.Description className={styles.description}>
                {body}
              </AlertDialog.Description>
            )}
            <div className={styles.footer}>
              {reverseButtons ? (
                <>
                  <AlertDialog.Action asChild>
                    <Button
                      colourVariant={confirmColourVariant}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        onConfirm?.(e)
                      }
                    >
                      {confirmLabel}
                    </Button>
                  </AlertDialog.Action>
                  <AlertDialog.Cancel asChild>
                    <Button
                      variant="outline"
                      colourVariant={cancelColourVariant}
                      className={styles.confirmButton}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        onCancel?.(e)
                      }
                    >
                      {cancelLabel}
                    </Button>
                  </AlertDialog.Cancel>
                </>
              ) : (
                <>
                  <AlertDialog.Cancel asChild>
                    <Button
                      variant="outline"
                      colourVariant={cancelColourVariant}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        onCancel?.(e)
                      }
                    >
                      {cancelLabel}
                    </Button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action asChild>
                    <Button
                      className={styles.confirmButton}
                      colourVariant={confirmColourVariant}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        onConfirm?.(e)
                      }
                    >
                      {confirmLabel}
                    </Button>
                  </AlertDialog.Action>
                </>
              )}
            </div>
          </motion.div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

export const Trigger = AlertDialog.Trigger
